"use client";

import React, { createContext, useContext, useEffect, useCallback } from "react";
import { useAuthStore } from "@/store/auth-store";
import { isAuthenticated as checkAuth, getValidToken, logout as authLogout } from "@/lib/auth/session";
import { authApi } from "@/lib/api/services/auth.api";
import type { User, AuthContextType, LoginCredentials, RegisterData } from "@/types/auth";
import { handleApiError } from "@/lib/api/errors";

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user, setUser, setTokens, setLoading, setError, clearAuth, isLoading } = useAuthStore();

  const fetchUser = useCallback(async () => {
    try {
      const token = await getValidToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const data = await authApi.getMe();
      setUser(data.user as User);
    } catch {
      setError("Failed to load user profile");
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setError(null);
    setLoading(true);
    try {
      const data = await authApi.login(credentials);
      setUser(data.user as User);
      setTokens(data.tokens as { accessToken: string; refreshToken: string; expiresIn: number });
    } catch (error) {
      const apiError = handleApiError(error);
      setError(apiError.message);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, [setUser, setTokens, setError, setLoading]);

  const register = useCallback(async (data: RegisterData) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authApi.register(data);
      setUser(response.user as User);
      setTokens(response.tokens as { accessToken: string; refreshToken: string; expiresIn: number });
    } catch (error) {
      const apiError = handleApiError(error);
      setError(apiError.message);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, [setUser, setTokens, setError, setLoading]);

  const logout = useCallback(() => {
    clearAuth();
    authLogout();
  }, [clearAuth]);

  const refreshSession = useCallback(async () => {
    try {
      const token = await getValidToken();
      if (!token) {
        logout();
        return;
      }
      const data = await authApi.getMe();
      setUser(data.user as User);
    } catch (error) {
      logout();
    }
  }, [setUser, logout]);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens: useAuthStore((state) => state.tokens),
        isAuthenticated: !!user && checkAuth(),
        isLoading,
        error: useAuthStore((state) => state.error),
        login,
        register,
        logout,
        refreshSession,
        clearError,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
